/**
 * Baseline Trajectory Screen (Step 4)
 * "If nothing changes…" — Current | Baseline | Future (placeholder).
 */
import React, { useState, useEffect } from 'react';
import { Form, InputNumber, Card, Row, Col, Button, Typography, Table } from 'antd';
import {
  getProductDomainFinancialSummary,
  getProductDomainPhaseData,
  upsertProductDomainPhaseData,
} from '../../services/productDomainSessionApi';

const { Text } = Typography;

interface BaselineTrajectoryScreenProps {
  sessionId: string;
  onNext: () => void;
}

export const BaselineTrajectoryScreen: React.FC<BaselineTrajectoryScreenProps> = ({
  sessionId,
  onNext,
}) => {
  const [financial, setFinancial] = useState<{ current_value_ratio: number; baseline_value_ratio?: number | null; growth_rate: number | null } | null>(null);
  const [transitionPeriods, setTransitionPeriods] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      const [finRes, phaseData] = await Promise.all([
        getProductDomainFinancialSummary(sessionId),
        getProductDomainPhaseData(sessionId),
      ]);
      const fs = finRes?.financial_summary;
      if (fs) {
        setFinancial({
          current_value_ratio: fs.current_value_ratio,
          baseline_value_ratio: fs.baseline_value_ratio ?? null,
          growth_rate: fs.growth_rate,
        });
      } else {
        setFinancial({ current_value_ratio: 0, growth_rate: null });
      }
      const future = phaseData?.phase_data?.future_target;
      if (future) {
        const t = future.find((i) => i.question_id === 'transition_duration_periods');
        if (t?.value_number != null) setTransitionPeriods(t.value_number);
      }
      setLoading(false);
    };
    load();
  }, [sessionId]);

  const handleSaveAndNext = async () => {
    if (transitionPeriods == null) return;
    setSaving(true);
    const ok = await upsertProductDomainPhaseData(sessionId, 'future_target', [
      { question_id: 'transition_duration_periods', value_type: 'number', value_number: transitionPeriods },
    ]);
    setSaving(false);
    if (ok) onNext();
  };

  if (loading) return <div>Loading...</div>;

  const currentVR = financial?.current_value_ratio ?? 0;
  const baselineVR = financial?.baseline_value_ratio ?? currentVR;

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <Typography.Title level={4}>If nothing changes…</Typography.Title>

      <Form layout="vertical" style={{ marginBottom: 24 }}>
        <Form.Item label="Transition duration (periods)">
          <InputNumber
            min={1}
            value={transitionPeriods ?? undefined}
            onChange={(v) => setTransitionPeriods(v ?? null)}
            style={{ width: 120 }}
          />
        </Form.Item>
      </Form>

      <Table
        dataSource={[
          { key: 'vr', metric: 'Value Ratio', current: currentVR.toFixed(2), baseline: baselineVR.toFixed(2), future: '—' },
        ]}
        columns={[
          { title: 'Metric', dataIndex: 'metric', key: 'metric' },
          { title: 'Current', dataIndex: 'current', key: 'current' },
          { title: 'Baseline', dataIndex: 'baseline', key: 'baseline' },
          { title: 'Future', dataIndex: 'future', key: 'future' },
        ]}
        pagination={false}
        size="small"
      />

      <Button
        type="primary"
        onClick={handleSaveAndNext}
        loading={saving}
        disabled={transitionPeriods == null}
        style={{ marginTop: 24 }}
      >
        Save & Next
      </Button>
    </div>
  );
};
